/* eslint-disable react/prop-types */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  StyledFileTypeDescription,
  StyledFileTypesBlock,
  StyledFileTypesComponentContainer,
  StyledFileTypesContainer,
} from './FileTypeComponent/styles';
import { componentFileTypesOnly } from './FileTypeComponent/fileTypes';
import { FileType, FileTypeName, Roles } from '@cambridgeassessment/lib-ee-commons';
import { StyledSectionContainer, StyledReadOnlyComponent } from './styles';
import { displayNotification } from '../../../../store/notification/actions';
import FileComponent from './FileTypeComponent/Component';
import { updateComponentSection } from '../../../../store/components/thunks';
import { ComponentSectionDoc, ComponentSections } from '../../../../service/components/types';
import { Authorized } from 'lib-browser';
import { RootState } from '../../../../store';

interface Props {
  componentSection: ComponentSectionDoc | ComponentSections;
  disabled?: boolean;
  componentId: string;
  children: React.ReactNode;
}

const ComponentSectionFileTypesFC: React.FC<Props> = ({
  componentSection,
  disabled,
  componentId,
  children,
}): React.ReactElement => {
  const dispatch = useDispatch();
  const selectedRole = useSelector((state: RootState) => state.userData.selectedRole);

  const updateFileTypes = (updatedComponent: ComponentSections): void => {
    dispatch(updateComponentSection(componentId, updatedComponent, 'fileTypes', updatedComponent.id));
  };

  const onFileTypeChange = (name: string, value: boolean): void => {
    const updatedComponent = { ...componentSection, fileTypes: componentSection.fileTypes || [] };
    if (value) {
      const fileTypeToUpdate = componentFileTypesOnly.find((item) => item.name === name);
      updatedComponent.fileTypes.push(fileTypeToUpdate as FileType);
    } else {
      const fileTypeToRemove = updatedComponent.fileTypes.findIndex((item) => item.name === name);
      updatedComponent.fileTypes.splice(fileTypeToRemove, 1);
    }

    updateFileTypes(updatedComponent as ComponentSections);
  };

  const showSnackbarForFileTypes = (isTheLastChecked: boolean): void => {
    if (isTheLastChecked && !disabled) {
      dispatch(displayNotification('At least one option must be present at all times'));
    }
  };

  const fileTypesDescription =
    'These types of files can be uploaded by the Exams Officer. At least one option must be present at all times';

  const renderFileType = (fileType: FileType, index: number): React.ReactElement => {
    const isChecked = !!componentSection.fileTypes?.find((item: FileType) => item.name === fileType.name);
    const isTheLastChecked = (componentSection.fileTypes?.length as number) <= 1 && isChecked;
    let subtext: string | undefined;
    if (fileType.name === FileTypeName.COMPRESSION) {
      subtext = '(Not supported by RMA3)';
    }

    return (
      <FileComponent
        fileType={fileType}
        checked={isChecked}
        disabled={disabled || isTheLastChecked}
        onChange={onFileTypeChange}
        isTheLastChecked={isTheLastChecked}
        showSnackbar={showSnackbarForFileTypes}
        dataQaId={`document-file-types-${componentSection.name}`}
        key={`${fileType.name}-${index}`}
        subtext={subtext}
      />
    );
  };

  const RenderedFiles = (props?: { readOnly?: boolean }): React.ReactElement => {
    return (
      <StyledReadOnlyComponent data-qa-id={`read-only-wrapper-${props?.readOnly || false}`} readOnly={props?.readOnly}>
        <StyledFileTypesContainer>{componentFileTypesOnly.map(renderFileType)}</StyledFileTypesContainer>
      </StyledReadOnlyComponent>
    );
  };

  return (
    <StyledFileTypesComponentContainer>
      <StyledSectionContainer>File types</StyledSectionContainer>
      <StyledFileTypesBlock disabled={disabled}>
        <StyledFileTypeDescription>{fileTypesDescription}</StyledFileTypeDescription>
        <Authorized
          selectedRole={selectedRole}
          roles={[Roles.SUBMIT_SUPER_ADMIN]}
          readOnlyRoles={[
            Roles.SUBMIT_COMPLIANCE,
            Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
            Roles.SUBMIT_ADMINISTRATOR,
          ]}
        >
          <RenderedFiles />
        </Authorized>
      </StyledFileTypesBlock>
      {children}
    </StyledFileTypesComponentContainer>
  );
};

export default ComponentSectionFileTypesFC;
